import { useEffect, useState } from 'react';
import freebeLogo from '../assets/integrations/freebe-logo.webp';
import googleDriveLogo from '../assets/integrations/google-drive-logo.png';
import trelloLogo from '../assets/integrations/trello-logo.png';
import '../styles/modules-connections.css';
import { connectorLogoApi, type ConnectorLogoRevision } from './connector-logo-api';

interface ConnectionsHubProps {
  readonly onOpenFreebe: () => void;
  readonly onOpenBnp: () => void;
  readonly onOpenDrive: () => void;
}

/**
 * Entrée unique des intégrations : chaque fournisseur conserve ensuite son écran et son historique.
 * Drive ouvre son parcours de connexion ; la disponibilité du compte vient ensuite du serveur.
 * Trello reste annoncé à venir tant que son connecteur n'est pas disponible.
 */
export function ConnectionsHub({
  onOpenFreebe,
  onOpenBnp,
  onOpenDrive,
}: ConnectionsHubProps): React.JSX.Element {
  const [logos, setLogos] = useState<readonly ConnectorLogoRevision[]>([]);
  useEffect(() => {
    void connectorLogoApi
      .list()
      .then(setLogos)
      .catch(() => undefined);
  }, []);
  return (
    <div className="app-content connections-hub">
      <header className="connections-hub__heading">
        <p className="dashboard-heading__eyebrow">Paramètres</p>
        <h1>Connexions</h1>
        <p>
          Choisissez le service à connecter. Chaque intégration dispose de ses propres réglages,
          états et historiques.
        </p>
      </header>

      <section className="connections-hub__grid" aria-label="Services disponibles">
        <button
          className="connections-hub__card is-available"
          type="button"
          onClick={onOpenFreebe}
          aria-label="Ouvrir la connexion Freebe"
        >
          <span className="connections-hub__logo-frame">
            <img src={connectorLogoApi.url('freebe', logos) ?? freebeLogo} alt="Freebe.me" />
          </span>
          <span className="connections-hub__card-footer">
            <span>
              <strong>Freebe</strong>
              <small>Clients et prospects</small>
            </span>
            <span className="connections-hub__status is-available">Disponible</span>
          </span>
        </button>

        <button
          className="connections-hub__card is-available"
          type="button"
          onClick={onOpenBnp}
          aria-label="Ouvrir la connexion BNP"
        >
          <span className="connections-hub__logo-frame">
            {connectorLogoApi.url('bnp', logos) ? (
              <img src={connectorLogoApi.url('bnp', logos)!} alt="BNP Paribas" />
            ) : (
              <span className="connections-hub__default-logo">BNP Paribas</span>
            )}
          </span>
          <span className="connections-hub__card-footer">
            <span>
              <strong>BNP Paribas</strong>
              <small>Compte professionnel · imports privés</small>
            </span>
            <span className="connections-hub__status is-available">Disponible</span>
          </span>
        </button>

        <article className="connections-hub__card is-upcoming" aria-label="Connexion Trello">
          <span className="connections-hub__logo-frame">
            <img src={connectorLogoApi.url('trello', logos) ?? trelloLogo} alt="Trello" />
          </span>
          <span className="connections-hub__card-footer">
            <span>
              <strong>Trello</strong>
              <small>Projets et tableaux</small>
            </span>
            <span className="connections-hub__status">À venir</span>
          </span>
        </article>

        <button
          className="connections-hub__card is-available"
          type="button"
          onClick={onOpenDrive}
          aria-label="Ouvrir la connexion Google Drive"
        >
          <span className="connections-hub__logo-frame">
            <img
              src={connectorLogoApi.url('google-drive', logos) ?? googleDriveLogo}
              alt="Google Drive"
            />
          </span>
          <span className="connections-hub__card-footer">
            <span>
              <strong>Google Drive</strong>
              <small>Drive de l’agence · connexion</small>
            </span>
            <span className="connections-hub__status is-available">Connexion</span>
          </span>
        </button>
      </section>
    </div>
  );
}
